import * as O from "fp-ts/Option"
import { pipe } from "fp-ts/function"
import cardsManifest from "@/data/document-generate-cards.json"

// Libellé du type d'un document affiché au portail apprenant. Trois familles
// de codes coexistent en base, aucune n'est réécrite :
// - les codes de modèle en minuscules (`attestation_fin`,
//   `certificat_realisation`…) que porte tout document généré depuis un
//   modèle (`document_type_for_template`, document_generation/numbering.rs) ;
// - les catégories de carte (`legal`, `evaluation`…) qu'envoyait l'écran de
//   génération avant, et que gardent les documents déjà émis ;
// - les codes libres historiques des documents de session (`convention`,
//   `program`, `attendance_sheet`…).
// Un code inconnu affiche « Document », jamais le code brut.

export const UNKNOWN_DOCUMENT_TYPE_LABEL = "Document"

/** Codes de modèle → libellé, lus dans le manifeste des cartes de génération (source unique des modèles proposés). */
const TEMPLATE_LABELS: ReadonlyMap<string, string> = new Map(
  cardsManifest.categories.flatMap((category) =>
    category.cards.map((card): [string, string] => [card.templateCode.toLowerCase(), card.label]),
  ),
)

/**
 * Codes historiques, figés : plus rien ne les produit, aucune source ne les
 * liste, mais les documents déjà émis les portent.
 */
const LEGACY_LABELS: ReadonlyMap<string, string> = new Map([
  ["convention", "Convention"],
  ["convocation", "Convocation"],
  ["program", "Programme"],
  ["attendance_sheet", "Émargement"],
  ["feuille_presence", "Feuille de présence"],
  ["attestation", "Attestation"],
  ["certificate", "Attestation"],
  ["invoice", "Facture"],
  ["evaluation", "Évaluation"],
  ["resource", "Ressource"],
  ["other", "Autre"],
  ["legal", "Document légal"],
  ["pedagogical", "Document pédagogique"],
  ["tracking", "Document de suivi"],
  ["qualiopi", "Document Qualiopi"],
])

/**
 * Le serveur libelle par `ref_document_type` et, faute d'entrée, renvoie le
 * code tel quel : ce repli n'est pas un libellé.
 */
function serverLabel(documentType: string, label: string): O.Option<string> {
  return pipe(
    O.some(label.trim()),
    O.filter((value) => value.length > 0 && value.toLowerCase() !== documentType.trim().toLowerCase()),
  )
}

export function portalDocumentTypeLabel(doc: { document_type: string; document_type_label: string }): string {
  const code = doc.document_type.trim().toLowerCase()
  return pipe(
    serverLabel(doc.document_type, doc.document_type_label),
    O.alt(() => O.fromNullable(TEMPLATE_LABELS.get(code))),
    O.alt(() => O.fromNullable(LEGACY_LABELS.get(code))),
    O.getOrElse(() => UNKNOWN_DOCUMENT_TYPE_LABEL),
  )
}

/**
 * Supports demandés au portail : la borne de dépôt d'une session
 * (`MAX_SESSION_DOCUMENTS`, session_documents.rs). Une session n'en porte
 * jamais plus : la liste du stagiaire n'est jamais tronquée.
 */
export const PORTAL_SUPPORTS_PER_PAGE = 100

/** Pastille du type de fichier d'un support (PDF, JPEG ou PNG au dépôt). */
export function portalFileIcon(mimeType: string): string {
  if (mimeType.includes("pdf")) return "PDF"
  if (mimeType.includes("image")) return "IMG"
  return "FICHIER"
}
